// Every visual token the sidebar spends, in one place — because both shapes
// draw the same nav and the two had already drifted apart once: the column's
// resting foreground was raised to /95 for legibility and the rail was left
// behind at /85, so the same icon changed brightness when you collapsed.

// ---------------------------------------------------------------------------
// The ramp, written once
// ---------------------------------------------------------------------------
// The documented brand-accent ramp (theming › Brand accent) has exactly two
// steps, and the sidebar spends them by altitude:
//
//   `/15`          — hover anywhere, and a *folded* group that contains the
//                    current route. It marks the section you are in, not the
//                    page you are on, so it stays the quiet step.
//   `/30` + ring   — the selected destination: a second-level child, or a
//                    single-page first-level row. It is the only thing naming
//                    where you actually are, so it is the loud step.
//
// Unfolding a group hands the loud step down to its child, which is why the
// group row drops to plain resting while it is open.

/**
 * Resting foreground for every first-level icon and label, in both shapes.
 *
 * Well short of full white, and deliberately so. The sidebar is on screen all
 * day beside the page you are actually working in, so its job is to stay
 * findable without competing for attention — white icons and labels read as
 * the loudest thing on the screen even when nothing is selected. What should
 * draw the eye is the selected row's accent surface.
 *
 * Shared with the rail, so an icon never changes brightness when the sidebar
 * collapses. Composites to about 8:1 on the navy — calmer, not less legible.
 */
export const restingFg = 'text-sidebar-foreground/75';

/** The one hover tint. */
export const hoverFill =
  'hover:bg-brand-accent/15 hover:text-sidebar-foreground';

/** The same tint where the hover target is a wrapper, as on the rail's pills. */
export const groupHoverFill =
  'group-hover:bg-brand-accent/15 group-hover:text-sidebar-foreground';

/** "You are somewhere in this section" — the quiet step, no ring. */
export const sectionSurface = 'bg-brand-accent/15 text-sidebar-foreground';

/** "You are on this page" — the loud step. */
export const selectedSurface =
  'bg-brand-accent/30 text-sidebar-foreground ring-1 ring-inset ring-brand-accent/40';

/** Icon metrics. Identical in both shapes, so collapsing resizes nothing. */
export const iconSize = 'size-[18px] min-[1600px]:size-5 shrink-0';

// ---------------------------------------------------------------------------
// Rhythm — the rail is the source, the column follows it
// ---------------------------------------------------------------------------
// The rail is a 36px pill on an 8px gap: a 44px pitch (40 + 14 from 1600px up).
// The column was built from padding instead, which landed at 32px with the rows
// touching, so the same nav read as tighter in the state that has more room.
//
// The row is therefore built as *the rail's pill plus a label*: `rowIcon` is the
// pill's exact box, so the icon keeps its x-position and the pitch matches, and
// collapsing the sidebar moves no icon. Section headings are the one thing that
// cannot align — the rail spends a hairline divider where the column spends a
// heading — so rows within a band share the grid and each band start shifts.
export const columnPad = 'px-2 min-[1600px]:px-2.5';
export const columnStack = 'flex flex-col gap-2 min-[1600px]:gap-3.5';

/** The rail's pill, exactly — this is what keeps the icon from moving. */
export const rowIcon =
  'grid place-items-center size-9 min-[1600px]:size-10 shrink-0';

// ---------------------------------------------------------------------------
// Level 1 — the structure you scan
// ---------------------------------------------------------------------------
// Size and weight carry the hierarchy, not opacity: white on `--sidebar` navy
// needs more weight than the same size would on a light ground, and 12px/400
// was the thinnest type in the product.
//
// Sized a step above the rest of the app's body copy on purpose. This is the
// one surface that is always on screen, always on a dark ground, and read by
// everyone all day — the people it fails first are the ones with the least
// comfortable eyesight, and a step up costs the column nothing it needs.
export const rowBase =
  'group flex w-full items-center rounded-lg pr-2 text-left cursor-pointer transition-colors h-9 min-[1600px]:h-10 text-sm min-[1600px]:text-base font-medium';

export const rowInactive = `${restingFg} ${hoverFill}`;

/** A folded group that owns the current route. */
export const rowInSection = sectionSurface;

/** The selected destination. */
export const rowSelected = selectedSurface;

// ---------------------------------------------------------------------------
// Level 2 — detail you read once you are there
// ---------------------------------------------------------------------------
// A step down in size and weight from level 1, so the two can never read as the
// same thing. It carries no icon, so its lower resting opacity is deliberate
// contrast between the levels rather than the drift the tokens above prevent.
export const childBase =
  'flex items-center rounded-lg px-2.5 cursor-pointer transition-colors h-[30px] min-[1600px]:h-8 text-xs min-[1600px]:text-sm';

export const childInactive = `text-sidebar-foreground/65 ${hoverFill}`;

export const childSelected = `${selectedSurface} font-semibold`;

/**
 * The expanded column's width. The grid in `__root.tsx` sizes the sidebar's
 * track in pixels while the column itself is sized by a class, so the two are
 * declared together here and must be changed together.
 *
 * 232px is set by "Certificate Compliance", the longest English label, which
 * needs 225px of column on wide screens — one step narrower (224px) clips it.
 * Long translations (fr/pt/es "Vendor Compliance") already ran past 248px and
 * truncate with an ellipsis; at 232px German joins them on wide screens.
 */
export const EXPANDED_SIDEBAR_WIDTH = 232;
export const expandedColumnWidthCls = 'w-58';
